import {
  FC,
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle, Plus, Undo2 } from 'lucide-react';
import {
  SERIALIZED,
  TOKEN_LABELS,
  TokenKind,
  canonicalizeExpression,
  findUnsupported,
  parseExpression,
  validateExpression,
} from './expressionTokens';
import { formatExpression, readingParts, segmentExpression } from './expressionHighlight';
import { ExpressionTester } from './ExpressionTester';

export interface ExpressionVariable {
  code: string;
  libelle: string;
  /** Unité déclarée sur le tunnel (euro, pourcent, m2…) : format par défaut des jetons {{CODE}} des documents. */
  unite?: string;
}

interface ExpressionBuilderProps {
  /** Expression sérialisée, ex. « if(VAR:BIEN_USAGE_PRO,1.1,1)*2 ». */
  value: string;
  onChange: (expression: string) => void;
  /** Formule à l'ouverture du formulaire : « Rétablir » y revient. */
  initialValue?: string;
  variables: ExpressionVariable[];
  disabled?: boolean;
}

/** Ce que la barre « Insérer » écrit au curseur, groupé comme la palette. */
const INSERT_GROUPS: Array<Exclude<TokenKind, 'value' | 'var'>[]> = [
  ['add', 'sub', 'mul', 'div'],
  ['eq', 'neq', 'lt', 'gt', 'lte', 'gte'],
  ['if', 'min', 'max', 'lparen', 'rparen', 'sep'],
];
const FUNCTION_KINDS: TokenKind[] = ['if', 'min', 'max'];

/** Touches consommées par la liste de suggestions : leur relâchement ne relance pas la détection. */
const SUGGEST_KEYS = ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'];

const MAX_SUGGESTIONS = 8;

const MONO = 'font-mono text-[13px] leading-6';

const SUGGESTIONS_WIDTH = 288;
const SUGGESTIONS_MAX_HEIGHT = 280;

/**
 * Fixed-position box anchored to the top-right corner of the editor, rendered at page
 * level: the editor card, the modal section and the modal body all clip their overflow,
 * which cut the list at their height. Placed above the editor when space is missing below.
 */
const anchorSuggestions = (editor: HTMLElement | null) => {
  if (!editor) return null;
  const rect = editor.getBoundingClientRect();
  const left = Math.max(8, Math.min(rect.right - SUGGESTIONS_WIDTH - 12, window.innerWidth - SUGGESTIONS_WIDTH - 8));
  const placeAbove = rect.top + 8 + SUGGESTIONS_MAX_HEIGHT > window.innerHeight && rect.top > SUGGESTIONS_MAX_HEIGHT;
  return placeAbove ? { left, bottom: window.innerHeight - rect.top + 4 } : { left, top: rect.top + 8 };
};

/**
 * Éditeur de formule de tarification, « code d'abord » : la formule est un texte coloré
 * saisi directement, mis en forme un facteur par ligne à la sortie du champ, avec
 * l'autocomplétion des variables du tunnel sur « VAR: ». La barre « Insérer » écrit au
 * curseur ; la ligne « Lecture » relit la formule en mots. Le contrôle complet est fait
 * par le back via le panneau de test.
 */
export const ExpressionBuilder: FC<ExpressionBuilderProps> = ({
  value,
  onChange,
  initialValue,
  variables,
  disabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<ReturnType<typeof anchorSuggestions>>(null);
  const isModified =
    initialValue !== undefined &&
    canonicalizeExpression(value) !== canonicalizeExpression(initialValue);
  const [pendingCaret, setPendingCaret] = useState<number | null>(null);

  // Autocomplétion : « VAR:xxx » juste avant le curseur, ou ouverte par le bouton Variable.
  const [suggest, setSuggest] = useState<{ query: string; start: number; end: number } | null>(
    null
  );

  useLayoutEffect(() => {
    if (!suggest) {
      setAnchor(null);
      return;
    }
    const update = () => setAnchor(anchorSuggestions(textareaRef.current));
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [suggest]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Keyboard navigation moves the active item; keep it visible inside the scrollable list.
  useEffect(() => {
    suggestionsRef.current
      ?.querySelector<HTMLElement>('[data-active]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const tokens = useMemo(() => parseExpression(value), [value]);
  const segments = useMemo(() => segmentExpression(value), [value]);
  const unsupported = useMemo(() => findUnsupported(value), [value]);
  const error = useMemo(
    () =>
      unsupported !== null
        ? `Élément non reconnu dans la formule : « ${unsupported} ».`
        : validateExpression(tokens),
    [tokens, unsupported]
  );

  const known = useMemo(() => new Set(variables.map((v) => v.code)), [variables]);
  const labelOf = useCallback(
    (code: string) => variables.find((v) => v.code === code)?.libelle,
    [variables]
  );

  const suggestions = useMemo(() => {
    if (!suggest) return [];
    const query = suggest.query.toLowerCase();
    return variables
      .filter(
        (v) => v.code.toLowerCase().includes(query) || v.libelle.toLowerCase().includes(query)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [suggest, variables]);

  // Le curseur est replacé après une insertion, une fois le texte rendu.
  useLayoutEffect(() => {
    if (pendingCaret === null || !textareaRef.current) return;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(pendingCaret, pendingCaret);
    setPendingCaret(null);
  }, [pendingCaret, value]);

  const detectSuggest = useCallback(() => {
    const area = textareaRef.current;
    if (!area || variables.length === 0 || area.selectionStart !== area.selectionEnd) {
      setSuggest(null);
      return;
    }
    const caret = area.selectionStart;
    const match = /VAR:([A-Za-z0-9_]*)$/.exec(area.value.slice(0, caret));
    if (!match) {
      setSuggest(null);
      return;
    }
    setSuggest({ query: match[1], start: match.index, end: caret });
    setActiveIndex(0);
  }, [variables.length]);

  /** Remplace [start, end[ par `text` et place le curseur après. */
  const replaceRange = (start: number, end: number, text: string) => {
    const next = value.slice(0, start) + text + value.slice(end);
    onChange(next);
    setPendingCaret(start + text.length);
    setSuggest(null);
  };

  const insertAtCaret = (text: string) => {
    if (disabled) return;
    const area = textareaRef.current;
    const start = area?.selectionStart ?? value.length;
    const end = area?.selectionEnd ?? value.length;
    replaceRange(start, end, text);
  };

  /** Ferme la liste ; un « VAR: » nu laissé par le bouton Variable est retiré. */
  const closeSuggest = () => {
    if (suggest && suggest.query === '' && value.slice(suggest.start, suggest.end) === 'VAR:') {
      replaceRange(suggest.start, suggest.end, '');
      return;
    }
    setSuggest(null);
  };

  const pickVariable = (variable: ExpressionVariable) => {
    if (!suggest) return;
    replaceRange(suggest.start, suggest.end, `VAR:${variable.code}`);
  };

  const openVariables = () => {
    if (disabled) return;
    const area = textareaRef.current;
    const caret = area?.selectionStart ?? value.length;
    // Écrire le préfixe : la liste s'ouvre dessus et se filtre au fil de la frappe.
    replaceRange(caret, area?.selectionEnd ?? caret, 'VAR:');
    setSuggest({ query: '', start: caret, end: caret + 4 });
    setActiveIndex(0);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!suggest || suggestions.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        break;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        pickVariable(suggestions[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        closeSuggest();
        break;
      default:
        break;
    }
  };

  /** À la sortie du champ : un facteur par ligne. Sans effet sur le sens de la formule. */
  const onBlur = () => {
    // Les boutons de la liste et de la barre annulent le mousedown : le champ garde le
    // focus quand on clique dessus, ce blur n'arrive que pour une vraie sortie.
    closeSuggest();
    const formatted = formatExpression(value);
    if (formatted !== value && !error) onChange(formatted);
  };

  /** Le relâchement d'une touche consommée par la liste ne doit pas la rouvrir. */
  const onKeyUp = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (SUGGEST_KEYS.includes(event.key)) return;
    detectSuggest();
  };

  useEffect(() => {
    if (disabled) setSuggest(null);
  }, [disabled]);

  const reading = useMemo(() => readingParts(tokens, labelOf), [tokens, labelOf]);

  return (
    <div className="flex flex-col gap-3">
      <div className="relative rounded-lg border border-gray-300 bg-white focus-within:border-primary">
        {/* Barre d'insertion */}
        <div className="flex flex-wrap items-center gap-2 rounded-t-lg border-b border-gray-100 bg-gray-50 px-2.5 py-1.5">
          <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-400">
            Insérer
          </span>
          {INSERT_GROUPS.map((group) => (
            <div key={group.join()} className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {group.map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    tabIndex={-1}
                    disabled={disabled}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => insertAtCaret(SERIALIZED[kind])}
                    className={
                      FUNCTION_KINDS.includes(kind)
                        ? 'inline-flex h-6 items-center rounded-md bg-violet-100 px-2 text-[13px] font-bold text-violet-700 hover:bg-violet-200 disabled:opacity-40'
                        : 'inline-flex h-6 min-w-[26px] items-center justify-center rounded-md border border-gray-200 bg-white px-1.5 font-mono text-[13px] text-gray-700 hover:bg-gray-100 disabled:opacity-40'
                    }
                  >
                    {TOKEN_LABELS[kind]}
                  </button>
                ))}
              </div>
              <span className="h-4 w-px bg-gray-200" />
            </div>
          ))}
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled || variables.length === 0}
            onMouseDown={(event) => event.preventDefault()}
            onClick={openVariables}
            className="inline-flex h-6 items-center gap-1 rounded-md bg-primary/10 px-2 text-[13px] font-bold text-primary hover:bg-primary/20 disabled:opacity-40"
          >
            <Plus className="h-3 w-3" strokeWidth={2.4} />
            Variable
          </button>
          {isModified && !disabled && (
            <button
              type="button"
              tabIndex={-1}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(initialValue ?? '');
                setSuggest(null);
              }}
              className="ml-auto inline-flex h-6 items-center gap-1 rounded-md px-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              title="Revenir à la formule telle qu’elle était à l’ouverture"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Rétablir
            </button>
          )}
        </div>

        {/* Corps : texte coloré sous un textarea au texte transparent, mêmes métriques */}
        <div className="flex items-stretch">
          <div className="relative min-w-0 flex-1">
            <pre
              aria-hidden
              className={`m-0 min-h-[4.5rem] whitespace-pre-wrap break-all px-3.5 py-3 text-gray-700 ${MONO}`}
            >
              {segments.map((segment, index) => {
                switch (segment.kind) {
                  case 'function':
                    return (
                      <span key={index} className="font-bold text-violet-700">
                        {segment.text}
                      </span>
                    );
                  case 'variable':
                    return (
                      <span
                        key={index}
                        className={
                          known.has(segment.code ?? '')
                            ? 'rounded bg-primary/10 text-primary'
                            : 'rounded bg-amber-100 text-amber-700'
                        }
                      >
                        {segment.text}
                      </span>
                    );
                  case 'number':
                    return (
                      <span key={index} className="text-teal-600">
                        {segment.text}
                      </span>
                    );
                  case 'punctuation':
                    return (
                      <span key={index} className="text-gray-400">
                        {segment.text}
                      </span>
                    );
                  case 'unknown':
                    return (
                      <span key={index} className="rounded bg-red-100 text-red-700">
                        {segment.text}
                      </span>
                    );
                  default:
                    return <span key={index}>{segment.text}</span>;
                }
              })}
              {/* Une ligne vide finale ne se voit pas dans un pre : on la matérialise. */}
              {value.endsWith('\n') ? '\n' : ''}
            </pre>
            <textarea
              ref={textareaRef}
              spellCheck={false}
              disabled={disabled}
              value={value}
              placeholder="Saisir la formule, ex. MAX(VAR:SURFACE*0.5,150)"
              onChange={(event) => onChange(event.target.value)}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              onClick={detectSuggest}
              onBlur={onBlur}
              className={`absolute inset-0 h-full w-full resize-none overflow-hidden whitespace-pre-wrap break-all bg-transparent px-3.5 py-3 text-transparent caret-gray-800 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed ${MONO}`}
            />

            {suggest &&
              suggestions.length > 0 &&
              anchor &&
              createPortal(
              <div
                style={{ ...anchor, width: SUGGESTIONS_WIDTH }}
                className="fixed z-[1000] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
              >
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-2.5 py-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                    Variables du tunnel
                  </span>
                  <span className="font-mono text-[10px] text-gray-400">VAR:</span>
                </div>
                <div ref={suggestionsRef} className="max-h-60 overflow-y-auto">
                {suggestions.map((variable, index) => (
                  <button
                    key={variable.code}
                    type="button"
                    tabIndex={-1}
                    data-active={index === activeIndex || undefined}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pickVariable(variable)}
                    className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left ${
                      index === activeIndex ? 'bg-primary/10' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="truncate text-[13px] text-gray-900">{variable.libelle}</span>
                    <span
                      className={`shrink-0 font-mono text-[11px] ${
                        index === activeIndex ? 'text-primary' : 'text-gray-400'
                      }`}
                    >
                      {variable.code}
                      {variable.unite ? ` · ${variable.unite}` : ''}
                    </span>
                  </button>
                ))}
                </div>
              </div>,
              document.body
            )}
          </div>
        </div>
      </div>

      {error && (
        <p className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}

      {reading.length > 0 && (
        <div className="flex items-start gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
          <span className="shrink-0 text-[11px] font-bold uppercase leading-5 tracking-[0.08em] text-gray-400">
            Lecture
          </span>
          <p className="m-0 text-xs leading-5 text-gray-500">
            {reading.map((part, index) => {
              switch (part.kind) {
                case 'function':
                  return (
                    <span key={index} className="font-bold text-violet-700">
                      {part.text}
                    </span>
                  );
                case 'variable':
                  return part.known ? (
                    <span key={index} className="font-bold text-gray-700">
                      {part.label}
                    </span>
                  ) : (
                    <span key={index} className="font-bold text-amber-700">
                      {part.code}
                      <span className="ml-1 rounded bg-amber-100 px-1 py-px text-[10px] font-bold uppercase tracking-wide text-amber-700">
                        hors tunnel
                      </span>
                    </span>
                  );
                case 'number':
                  return (
                    <span key={index} className="text-teal-600">
                      {part.text}
                    </span>
                  );
                case 'operator':
                  return (
                    <span key={index} className="text-gray-500">
                      {' '}
                      {part.text}{' '}
                    </span>
                  );
                default:
                  return (
                    <span key={index} className="text-gray-400">
                      {part.text}
                      {part.text === ',' ? ' ' : ''}
                    </span>
                  );
              }
            })}
          </p>
        </div>
      )}

      <ExpressionTester
        expression={value}
        tokens={tokens}
        variables={variables}
        disabled={disabled}
      />
    </div>
  );
};
